import { MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Extract a WhatsApp-compatible phone number from a free-form contact string.
 * Returns digits only (no +, no spaces). Requires 8+ digits and drops a leading 00.
 * Examples:
 *   "+39 333 1234567" → "393331234567"
 *   "Mario Rossi · +39 333 1234567" → "393331234567"
 *   "0039 3331234567" → "393331234567"
 *   "mario@email.com" → null
 */
export function extractPhone(input) {
  if (!input) return null;
  // Grab the longest digit run possibly interrupted by ' ', '-', '.', '(', ')', '+'
  const matches = String(input).match(/[+\d][\d\s\-.()]{6,}/g) || [];
  for (const raw of matches) {
    let digits = raw.replace(/\D/g, "");
    if (digits.startsWith("00")) digits = digits.slice(2);
    if (digits.length >= 8 && digits.length <= 15) return digits;
  }
  return null;
}

const GREETINGS = {
  it: (ticketNumber) => `Buongiorno, sono un tecnico Wineemotion e la contatto in merito al ticket ${ticketNumber}. Come posso aiutarla?`,
  en: (ticketNumber) => `Hello, I'm a Wineemotion technician contacting you about ticket ${ticketNumber}. How may I help you?`,
  fr: (ticketNumber) => `Bonjour, je suis un technicien Wineemotion et je vous contacte concernant le ticket ${ticketNumber}. Comment puis-je vous aider ?`,
  es: (ticketNumber) => `Buenos días, soy un técnico de Wineemotion y le contacto en relación al ticket ${ticketNumber}. ¿En qué puedo ayudarle?`,
};

/**
 * Green button that opens a wa.me click-to-chat URL in a new tab.
 * Uses the ticket's reporter_contact / legacy reported_by string / customer phone
 * to find a WhatsApp phone number. Message is pre-filled in the customer's
 * preferred language (falls back to Italian).
 *
 * Renders nothing if no phone can be extracted.
 */
export function WhatsAppChatButton({ ticket, className = "", size = "sm" }) {
  const phone =
    extractPhone(ticket?.whatsapp_from) ||
    extractPhone(ticket?.reporter_contact) ||
    extractPhone(ticket?.reported_by) ||
    extractPhone(ticket?.dispenser?.end_customer_phone) ||
    extractPhone(ticket?.dispenser?.customer_phone);

  if (!phone) return null;

  const lang = (ticket?.customer_language || "it").toLowerCase();
  const buildMsg = GREETINGS[lang] || GREETINGS.it;
  const greeting = buildMsg(ticket?.ticket_number || "");
  const url = `https://wa.me/${phone}?text=${encodeURIComponent(greeting)}`;

  return (
    <Button
      asChild
      size={size}
      data-testid="wa-chat-btn"
      className={`bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm ${className}`}
    >
      <a href={url} target="_blank" rel="noreferrer" title={`Chatta su WhatsApp con +${phone}`}>
        <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
        Chatta su WhatsApp
      </a>
    </Button>
  );
}
